/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { DROPDOWN_STYLE, IDropdownMenuItem } from "../../../../ui/components/DropdownMenu";
import { ToolbarButton, ToolbarButtonProps } from "../../../../ui/components/Toolbar/ToolbarButton";
import { ToolbarCommandButton } from "../../../../ui/components/Toolbar/ToolbarCommandButton";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { README_EDITOR_VIEW_MODE } from "../../types";
import { ReadmeEditorView } from "../ReadmeEditorView";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { FieldButton, FieldGroup } from "@adapptio/lib-editor-ui";
import { useAbility } from "../../../../ui/hooks/useAbility";
import { ABILITY_ACTIONS } from "../../../../ability/AbilityService";
import { FC_ANCHOR } from "../../../../ui/components/FloatingContainer";
import { useDeepCompareMemo } from "use-deep-compare";
import { CMD } from "../../../../commands/CMD";
import { useLayoutElement } from "../../../../ui/components/Layout/useLayoutElement";
import { LayoutState_Column } from "../../../../ui/components/Layout/LayoutState";

export interface IReadmeEditorToolbarProps {
	view: ReadmeEditorView;
}

const viewModeMenuItems: IDropdownMenuItem[] = [
	{
		id: README_EDITOR_VIEW_MODE.VIEW,
		label: "View",
		icon: "mdi/eye"
	},
	{
		id: README_EDITOR_VIEW_MODE.EDIT,
		label: "Edit",
		icon: "mdi/pencil"
	},
	{
		id: README_EDITOR_VIEW_MODE.DEBUG,
		label: "Debug",
		icon: "mdi/bug"
	}
];

export const ReadmeEditorToolbar: FC<IReadmeEditorToolbarProps> = ({ view }) => {
	const ability = useAbility();
	const layout = view.getLayout();

	const [ viewMode, setViewMode ] = useState(() => view.getMode());

	const [ resourceInfo, setResourceInfo ] = useState(() => ({
		resource: view.getResource(),
		parentList: view.getResourceParentList(),
		isBeingSaved: view.isBeingSaved()
	}));

	const [ viewInfo, setViewInfo ] = useState(view.getViewInfo());

	const panelLeft = useLayoutElement<LayoutState_Column>(layout.getById("panelLeft"));
	const panelRight = useLayoutElement<LayoutState_Column>(layout.getById("panelRight"));

	const selectedViewModeMenuItem = useMemo(
		() => viewModeMenuItems.find((item) => item.id === viewMode),
		[ viewMode ]
	);

	const handleSelectViewMode = useCallback<ToolbarButtonProps["onMenuItemClick"]>((item) => {
		view.setMode(item.id as README_EDITOR_VIEW_MODE);
	}, [ view ]);

	useSimpleEvent(
		view.onModeChange,
		() => {
			setViewMode(view.getMode());
		},
		[ view ]
	);

	const saveAllowed = useDeepCompareMemo(() => view.canSave(), [ viewInfo, resourceInfo ]);

	const [ undoRedoEnabled, setUndoRedoEnabled ] = useState({
		undo: view.canUndo(),
		redo: view.canRedo()
	});

	useSimpleEvent(
		view.getModel().onModelChange,
		() => {
			setUndoRedoEnabled({
				undo: view.canUndo(),
				redo: view.canRedo()
			});
		},
		[ view ]
	);

	useEffect(() => {
		const handleResourceInfoChange = () =>
			setResourceInfo({
				resource: view.getResource(),
				parentList: view.getResourceParentList(),
				isBeingSaved: view.isBeingSaved()
			});

		const handleViewInfoChange = () => {
			setViewInfo(view.getViewInfo());
		};

		onEvent(view.onResourceInfoChanged, handleResourceInfoChange);
		onEvent(view.onViewInfoChanged, handleViewInfoChange);
		return () => {
			offEvent(view.onResourceInfoChanged, handleResourceInfoChange);
			offEvent(view.onViewInfoChanged, handleViewInfoChange);
		};
	}, [ view ]);

	return (
		<>
			<ToolbarButton
				icon="mdi/dock-left"
				active={panelLeft.isExpanded}
				onClick={panelLeft.toggleExpanded}
				tooltip="Toggle Pages Panel"
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/undo-variant"
				disabled={!undoRedoEnabled.undo}
				commandId={CMD.README_EDITOR.EDIT.UNDO}
				tooltip="Undo"
			/>

			<ToolbarCommandButton
				icon="mdi/redo-variant"
				disabled={!undoRedoEnabled.redo}
				commandId={CMD.README_EDITOR.EDIT.REDO}
				tooltip="Redo"
			/>

			<ToolbarSpacer grow={1} />

			<FieldGroup>
				{viewModeMenuItems.map((item, index) => (
					<FieldButton
						key={index}
						icon={item.icon}
						tooltip={item.label}
						selected={item.id === selectedViewModeMenuItem.id}
						value={item.id}
						onClick={() => handleSelectViewMode(item)}
					/>
				))}
			</FieldGroup>

			<ToolbarSpacer grow={1} />

			<ToolbarCommandButton
				icon="mdi/content-save"
				commandId={CMD.README_EDITOR.EDIT.SAVE}
				disabled={!saveAllowed}
				tooltip="Save"
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/menu"
				tooltip="More options"
				menuItems={[
					{
						id: "rename",
						label: "Rename",
						icon: "mdi/pencil",
						commandId: "resource.rename",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.RENAME, resourceInfo.resource)
					},
					{
						id: "move",
						label: "Move",
						icon: "mdi/folder-move",
						commandId: "resource.moveDialog.open",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.RENAME, resourceInfo.resource)
					},
					{
						id: "duplicate",
						label: "Duplicate",
						icon: "mdi/content-copy",
						commandId: "resource.duplicate",
						commandArgs: [ resourceInfo.resource?.id, true ]
					},
					{
						id: "history",
						label: "Revision History",
						icon: "mdi/history",
						commandId: "resource.openRevisionHistory",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: true
					},
					{
						id: "export",
						label: "Export to YAML",
						icon: "mdi/export",
						commandId: "resource.export",
						commandArgs: [ resourceInfo.resource?.id ]
					},
					{
						id: "delete",
						label: "Delete",
						icon: "mdi/delete",
						commandId: "resource.delete",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.REMOVE, resourceInfo.resource)
					}
				]}
				menuAnchor={FC_ANCHOR.RIGHT_BOTTOM}
				menuStyle={DROPDOWN_STYLE.COMPACT}
			/>

			<ToolbarSpacer />

			<ToolbarButton
				icon="mdi/dock-right"
				active={panelRight.isExpanded}
				onClick={panelRight.toggleExpanded}
				tooltip="Toggle Inspector Panel"
			/>
		</>
	);
};
